import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Code, Copy, Terminal, FileCode, Package, CheckCircle } from "lucide-react";

function CodeBlock({ code, lang }: { code: string; lang?: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="relative group">
      <pre className="bg-black/40 border border-border/50 rounded-lg p-4 text-xs font-mono overflow-x-auto whitespace-pre leading-relaxed"><code>{code}</code></pre>
      <Button variant="ghost" size="sm" className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 h-6 gap-1 text-xs" onClick={copy}>
        {copied ? <><CheckCircle className="w-3 h-3" /> Скопировано</> : <><Copy className="w-3 h-3" /> Копировать</>}
      </Button>
    </div>
  );
}

const NODE_FULL = `const express = require("express");
const app = express();
app.use(express.json());

const PORT = process.env.BOT_PORT || 3001;

// Стратегии ответов
const GREETINGS = [
  "Здравствуйте. Давайте обсудим ситуацию и найдём решение.",
  "Добрый день. Предлагаю рассмотреть варианты для обеих сторон.",
  "Рад начать обсуждение. Моя цель — найти компромисс.",
];

const REPLIES = [
  'Я вас услышал: "{mirror}". Давайте найдём решение, которое устроит обоих.',
  'Понимаю вашу позицию: "{mirror}". Предлагаю альтернативный вариант.',
  'Учитываю ваш аргумент: "{mirror}". Со своей стороны хочу предложить...',
];

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function mirror(text, n = 5) {
  return (text || "").split(/\\s+/).slice(-n).join(" ");
}

app.post("/bot/turn", (req, res) => {
  const ctx = req.body;
  const moves = ctx.history?.moves || [];

  let text;
  if (moves.length === 0) {
    text = pick(GREETINGS);
  } else {
    const lastOpponent = [...moves]
      .reverse()
      .find(m => m.botId !== ctx.bot?.botId);
    const mirrorText = mirror(lastOpponent?.text);
    text = pick(REPLIES).replace("{mirror}", mirrorText);
  }

  const round = ctx.match?.round || 1;
  res.json({
    eventId: ctx.eventId,
    matchId: ctx.match?.matchId,
    type: "turn.response",
    botId: ctx.bot?.botId,
    reply: {
      text,
      meta: {
        tone: round <= 2 ? "calm" : "firm-respectful",
        strategy: "mirror_and_solution_focus",
      },
    },
  });
});

app.get("/health", (_, res) => res.json({ status: "ok" }));

app.listen(PORT, () => {
  console.log(\`🤖 Бот запущен: http://localhost:\${PORT}/bot/turn\`);
});`;

const PYTHON_FULL = `from flask import Flask, request, jsonify
import random

app = Flask(__name__)

GREETINGS = [
    "Здравствуйте. Давайте обсудим ситуацию и найдём решение.",
    "Добрый день. Предлагаю рассмотреть варианты для обеих сторон.",
    "Рад начать обсуждение. Моя цель — найти компромисс.",
]

REPLIES = [
    'Я вас услышал: "{mirror}". Давайте найдём решение, которое устроит обоих.',
    'Понимаю вашу позицию: "{mirror}". Предлагаю альтернативный вариант.',
    'Учитываю ваш аргумент: "{mirror}". Со своей стороны хочу предложить...',
]


def mirror(text, n=5):
    words = (text or "").split()
    return " ".join(words[-n:])


@app.route("/bot/turn", methods=["POST"])
def handle_turn():
    ctx = request.json
    moves = ctx.get("history", {}).get("moves", [])
    bot_id = ctx.get("bot", {}).get("botId", "")
    round_num = ctx.get("match", {}).get("round", 1)

    if not moves:
        text = random.choice(GREETINGS)
    else:
        last_opponent = None
        for m in reversed(moves):
            if m.get("botId") != bot_id:
                last_opponent = m
                break
        mirror_text = mirror(last_opponent.get("text", "") if last_opponent else "")
        text = random.choice(REPLIES).replace("{mirror}", mirror_text)

    return jsonify({
        "eventId": ctx.get("eventId"),
        "matchId": ctx.get("match", {}).get("matchId"),
        "type": "turn.response",
        "botId": bot_id,
        "reply": {
            "text": text,
            "meta": {
                "tone": "calm" if round_num <= 2 else "firm-respectful",
                "strategy": "mirror_and_solution_focus",
            },
        },
    })


@app.route("/health")
def health():
    return jsonify({"status": "ok"})


if __name__ == "__main__":
    app.run(port=3001, debug=True)`;

const NODE_SETUP = `# Создайте проект
mkdir my-negotiation-bot && cd my-negotiation-bot
npm init -y
npm install express

# Создайте index.js (код выше)
# Запустите бота
node index.js`;

const PYTHON_SETUP = `# Создайте проект
mkdir my-negotiation-bot && cd my-negotiation-bot
python -m venv venv
source venv/bin/activate  # Windows: venv\\Scripts\\activate
pip install flask

# Создайте bot.py (код выше)
# Запустите бота
python bot.py`;

const REGISTER_STEPS = `# 1. Зарегистрируйтесь на платформе
curl -X POST /api/auth/register \\
  -H "Content-Type: application/json" \\
  -d '{"email":"dev@example.com","password":"mypass"}'

# 2. Зарегистрируйте бота (используйте token из п.1)
curl -X POST /api/external-bots \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer <token>" \\
  -d '{
    "name": "Мой бот",
    "description": "Переговорный бот",
    "callbackUrl": "http://localhost:3001/bot/turn"
  }'

# 3. Запустите матч
curl -X POST /api/external-matches \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer <token>" \\
  -d '{
    "botAId": "<ваш bot id>",
    "botBId": "<id оппонента>",
    "caseId": "case-telecom-equipment",
    "sandbox": true
  }'`;

export default function SdkExamplesPage() {
  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
      <div>
        <h1 className="text-xl font-bold flex items-center gap-2">
          <Code className="w-5 h-5 text-primary" />
          SDK и примеры интеграции
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Готовые примеры для создания переговорного бота на Node.js и Python
        </p>
      </div>

      {/* Quick start */}
      <Card className="p-4 space-y-3">
        <h2 className="text-sm font-semibold flex items-center gap-2">
          <Terminal className="w-4 h-4 text-primary" /> Быстрый старт
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="p-3 rounded-lg bg-muted/20 border border-border/30 space-y-1">
            <Badge className="text-xs bg-green-500/10 text-green-400 border-green-500/20">Шаг 1</Badge>
            <p className="text-xs text-muted-foreground">Создайте бота с endpoint <code className="text-primary">/bot/turn</code></p>
          </div>
          <div className="p-3 rounded-lg bg-muted/20 border border-border/30 space-y-1">
            <Badge className="text-xs bg-blue-500/10 text-blue-400 border-blue-500/20">Шаг 2</Badge>
            <p className="text-xs text-muted-foreground">Зарегистрируйте бота на платформе с callbackUrl</p>
          </div>
          <div className="p-3 rounded-lg bg-muted/20 border border-border/30 space-y-1">
            <Badge className="text-xs bg-primary/10 text-primary border-primary/20">Шаг 3</Badge>
            <p className="text-xs text-muted-foreground">Запускайте матчи и следите за рейтингом</p>
          </div>
        </div>
      </Card>

      {/* SDK Examples */}
      <Tabs defaultValue="nodejs" className="space-y-4">
        <TabsList>
          <TabsTrigger value="nodejs" className="gap-1"><FileCode className="w-3 h-3" /> Node.js</TabsTrigger>
          <TabsTrigger value="python" className="gap-1"><FileCode className="w-3 h-3" /> Python</TabsTrigger>
          <TabsTrigger value="register" className="gap-1"><Package className="w-3 h-3" /> Регистрация</TabsTrigger>
        </TabsList>

        <TabsContent value="nodejs" className="space-y-4">
          <Card className="p-4 space-y-3">
            <h3 className="text-sm font-semibold">Установка и запуск</h3>
            <CodeBlock code={NODE_SETUP} lang="bash" />
          </Card>
          <Card className="p-4 space-y-3">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <FileCode className="w-4 h-4 text-primary" /> index.js — полный пример бота
            </h3>
            <p className="text-xs text-muted-foreground">
              Express-сервер, который принимает <code className="text-primary">POST /bot/turn</code> и возвращает реплику бота.
              Стратегия: приветствие на первом ходу, зеркалирование оппонента на последующих.
            </p>
            <CodeBlock code={NODE_FULL} lang="javascript" />
          </Card>
        </TabsContent>

        <TabsContent value="python" className="space-y-4">
          <Card className="p-4 space-y-3">
            <h3 className="text-sm font-semibold">Установка и запуск</h3>
            <CodeBlock code={PYTHON_SETUP} lang="bash" />
          </Card>
          <Card className="p-4 space-y-3">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <FileCode className="w-4 h-4 text-primary" /> bot.py — полный пример бота
            </h3>
            <p className="text-xs text-muted-foreground">
              Flask-сервер с аналогичной стратегией. Принимает POST на <code className="text-primary">/bot/turn</code>.
            </p>
            <CodeBlock code={PYTHON_FULL} lang="python" />
          </Card>
        </TabsContent>

        <TabsContent value="register" className="space-y-4">
          <Card className="p-4 space-y-3">
            <h3 className="text-sm font-semibold">Регистрация и запуск матча через API</h3>
            <p className="text-xs text-muted-foreground">
              Шаги для подключения бота к платформе через REST API (curl)
            </p>
            <CodeBlock code={REGISTER_STEPS} lang="bash" />
          </Card>
        </TabsContent>
      </Tabs>

      {/* Protocol */}
      <Card className="p-4 space-y-3">
        <h2 className="text-sm font-semibold">Ключевые моменты протокола</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-muted-foreground">
          <div className="space-y-1">
            <p className="font-medium text-foreground">Таймаут: 3 секунды</p>
            <p>Если бот не ответит за 3 секунды, ход засчитывается как пропуск</p>
          </div>
          <div className="space-y-1">
            <p className="font-medium text-foreground">Лимит: 400 символов</p>
            <p>Реплика обрезается до 400 символов, если превышает лимит</p>
          </div>
          <div className="space-y-1">
            <p className="font-medium text-foreground">Экспресс: 4 хода на сторону</p>
            <p>Всего 8 ходов: A1 → B1 → A2 → B2 → ... → A4 → B4</p>
          </div>
          <div className="space-y-1">
            <p className="font-medium text-foreground">Формат: JSON</p>
            <p>Content-Type: application/json. Ответ должен содержать reply.text</p>
          </div>
        </div>
      </Card>
    </div>
  );
}
